import { useEffect, useState, useCallback } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import LinkExtension from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import {
  TextStyle,
  Color,
  FontSize,
  BackgroundColor,
} from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo2,
  Redo2,
  Link as LinkIcon,
  ImagePlus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Highlighter,
} from "lucide-react";
import "../cms/CMS.css";
import "./MoreInfoSupport.css";
import { apiGet, apiPost } from "../../Services/Services";
import { ENDPOINTS } from "../../Services/Endpoints";
import {
  buildAboutPayload,
  buildFaqPayload,
  buildTextPagePayload,
} from "./moreInfoFormData";

const SECTION_OPTIONS = [
  { value: "terms", label: "Terms and Conditions" },
  { value: "privacy", label: "Privacy Policy" },
  { value: "about", label: "About" },
  { value: "faq", label: "FAQ" },
];

const emptyFaqItem = () => ({ question: "", answer: "" });

// common extensions used by all rich text editors
const editorExtensions = (placeholderText) => [
  StarterKit,
  Underline,
  TextStyle,
  Color,
  FontSize,
  BackgroundColor,
  Highlight.configure({ multicolor: true }),
  LinkExtension.configure({ openOnClick: false }),
  Image,
  Placeholder.configure({ placeholder: placeholderText }),
  TextAlign.configure({ types: ["heading", "paragraph"] }),
];

const MoreInfoSupport = () => {
  const [pageType, setPageType] = useState("");
  const [sectionError, setSectionError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [termsForm, setTermsForm] = useState({ title: "", content: "" });
  const [privacyForm, setPrivacyForm] = useState({ title: "", content: "" });
  const [aboutForm, setAboutForm] = useState({ title: "", description: "" });
  const [faqItems, setFaqItems] = useState([emptyFaqItem()]);

  const [contentErrors, setContentErrors] = useState({
    content: "",
    description: "",
    faq: [],
  });

  // ---------- Tiptap editors ----------
  const termsEditor = useEditor({
    extensions: editorExtensions("Enter terms and conditions content"),
    content: termsForm.content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setTermsForm((prev) => ({ ...prev, content: html }));
      setContentErrors((prev) => ({ ...prev, content: "" }));
    },
  });

  const privacyEditor = useEditor({
    extensions: editorExtensions("Enter privacy policy content"),
    content: privacyForm.content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setPrivacyForm((prev) => ({ ...prev, content: html }));
      setContentErrors((prev) => ({ ...prev, content: "" }));
    },
  });

  const aboutEditor = useEditor({
    extensions: editorExtensions("Enter about us content"),
    content: aboutForm.description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setAboutForm((prev) => ({ ...prev, description: html }));
      setContentErrors((prev) => ({ ...prev, description: "" }));
    },
  });

  const applySectionData = (type, data) => {
    const responseBody = data?.data?.data !== undefined ? data.data : data;
    const payload = responseBody?.data ?? responseBody ?? {};
    const htmlContent =
      typeof payload === "string"
        ? payload
        : payload.content ?? payload.description ?? "";

    switch (type) {
      case "terms": {
        setTermsForm((prev) => ({
          title: typeof payload === "object" ? payload.title ?? prev.title : prev.title,
          content: htmlContent,
        }));
        termsEditor?.commands.setContent(htmlContent, false);
        break;
      }
      case "privacy": {
        setPrivacyForm((prev) => ({
          title: typeof payload === "object" ? payload.title ?? prev.title : prev.title,
          content: htmlContent,
        }));
        privacyEditor?.commands.setContent(htmlContent, false);
        break;
      }
      case "about": {
        const description = payload.description ?? payload.content ?? "";
        setAboutForm({ title: payload.title ?? "", description });
        aboutEditor?.commands.setContent(description);
        break;
      }
      case "faq": {
        const items = payload.items ?? payload.faqs ?? payload.data ?? [];
        setFaqItems(
          Array.isArray(items) && items.length
            ? items.map((item) => ({
                question: item.question ?? "",
                answer: item.answer ?? "",
              }))
            : [emptyFaqItem()]
        );
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (pageType !== "terms" && pageType !== "privacy") return;

    const editor = pageType === "terms" ? termsEditor : privacyEditor;
    if (!editor) return;

    let isCancelled = false;

    const fetchPageContent = async () => {
      setLoading(true);

      try {
        const endpoint =
          pageType === "terms"
            ? ENDPOINTS.moreInfoSupport.terms
            : ENDPOINTS.moreInfoSupport.privacy;

        const response = await apiGet(endpoint);

        if (!isCancelled) {
          applySectionData(pageType, response);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error(`Unable to fetch ${pageType} content:`, error);
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchPageContent();

    return () => {
      isCancelled = true;
    };
  }, [pageType, termsEditor, privacyEditor]);

  const handleSectionChange = (e) => {
    const value = e.target.value;
    setPageType(value);
    if (value) setSectionError("");
  };

  const updateFaqItem = (index, field, value) => {
    const updated = [...faqItems];
    updated[index][field] = value;
    setFaqItems(updated);
    setContentErrors((prev) => {
      const nextFaq = [...(prev.faq || [])];
      nextFaq[index] = { ...(nextFaq[index] || {}), [field]: "" };
      return { ...prev, faq: nextFaq };
    });
  };

  const addFaqItem = () => setFaqItems([...faqItems, emptyFaqItem()]);

  const removeFaqItem = (index) => {
    if (faqItems.length === 1) return;
    setFaqItems(faqItems.filter((_, i) => i !== index));
  };

  const handleEditorImageUpload = useCallback((editor, e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;

    const reader = new FileReader();
    reader.onload = () => {
      editor.chain().focus().setImage({ src: reader.result }).run();
    };
    reader.readAsDataURL(file);

    e.target.value = "";
  }, []);

  const handleSave = async () => {
    if (!pageType) {
      setSectionError("Please select a page section.");
      return;
    }

    let payload = null;
    let endpoint = "";

    if (pageType === "terms") {
      const plainText = termsEditor?.getText().trim() || "";
      const contentError = plainText ? "" : "Please enter content.";

      setContentErrors({ content: contentError, description: "", faq: [] });

      if (contentError) return;

      payload = buildTextPagePayload(termsForm);
      endpoint = ENDPOINTS.moreInfoSupport.terms;
    } else if (pageType === "privacy") {
      const plainText = privacyEditor?.getText().trim() || "";
      const contentError = plainText ? "" : "Please enter content.";

      setContentErrors({ content: contentError, description: "", faq: [] });

      if (contentError) return;

      payload = buildTextPagePayload(privacyForm);
      endpoint = ENDPOINTS.moreInfoSupport.privacy;
    } else if (pageType === "about") {
      const plainText = aboutEditor?.getText().trim() || "";
      const descriptionError = plainText ? "" : "Please enter about description.";

      setContentErrors({ content: "", description: descriptionError, faq: [] });

      if (descriptionError) return;

      payload = buildAboutPayload(aboutForm);
      endpoint = ENDPOINTS.moreInfoSupport.about;
    } else if (pageType === "faq") {
      const faqErrors = faqItems.map((item) => ({
        question: item.question.trim() ? "" : "Please enter question.",
        answer: item.answer.trim() ? "" : "Please enter answer.",
      }));

      setContentErrors({ content: "", description: "", faq: faqErrors });

      const hasFaqErrors = faqErrors.some(
        (item) => item.question || item.answer
      );

      if (hasFaqErrors) return;

      payload = buildFaqPayload(faqItems);
      endpoint = ENDPOINTS.moreInfoSupport.faq;
    }

    if (!payload || !endpoint) return;

    setSaving(true);

    try {
      await apiPost(
        endpoint,
        payload,
        {},
        {
          showSuccessToast: true,
          useApiSuccessMessage: true,
        }
      );
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const selectedLabel =
    SECTION_OPTIONS.find((opt) => opt.value === pageType)?.label ?? "Page";

  // ---------- shared toolbar renderer ----------
  const renderToolbar = (editor) => (
    <div className="editor-toolbar">
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleUnderline().run()} className={editor?.isActive("underline") ? "active" : ""}>
        <UnderlineIcon size={18} />
      </button>
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleBold().run()} className={editor?.isActive("bold") ? "active" : ""}>
        <Bold size={18} />
      </button>
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleItalic().run()} className={editor?.isActive("italic") ? "active" : ""}>
        <Italic size={18} />
      </button>

      <select
        disabled={!editor}
        defaultValue=""
        onChange={(e) => {
          if (!e.target.value) return;
          editor?.chain().focus().setFontSize(e.target.value).run();
        }}
      >
        <option value="" disabled>Size</option>
        <option value="12px">12</option>
        <option value="14px">14</option>
        <option value="16px">16</option>
        <option value="18px">18</option>
        <option value="20px">20</option>
        <option value="24px">24</option>
        <option value="28px">28</option>
        <option value="32px">32</option>
      </select>

      <label className="editor-color-label">
        <span>Text</span>
        <input type="color" disabled={!editor} onChange={(e) => editor?.chain().focus().setColor(e.target.value).run()} />
      </label>
      <label className="editor-color-label">
        <span>BG</span>
        <input type="color" disabled={!editor} onChange={(e) => editor?.chain().focus().setBackgroundColor(e.target.value).run()} />
      </label>

      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()} className={editor?.isActive("heading", { level: 1 }) ? "active" : ""}>
        <Heading1 size={18} />
      </button>
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} className={editor?.isActive("heading", { level: 2 }) ? "active" : ""}>
        <Heading2 size={18} />
      </button>

      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleBulletList().run()} className={editor?.isActive("bulletList") ? "active" : ""}>
        <List size={18} />
      </button>
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleOrderedList().run()} className={editor?.isActive("orderedList") ? "active" : ""}>
        <ListOrdered size={18} />
      </button>

      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().setTextAlign("left").run()}>
        <AlignLeft size={18} />
      </button>
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().setTextAlign("center").run()}>
        <AlignCenter size={18} />
      </button>
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().setTextAlign("right").run()}>
        <AlignRight size={18} />
      </button>

      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().toggleHighlight().run()} className={editor?.isActive("highlight") ? "active" : ""}>
        <Highlighter size={18} />
      </button>

      <button
        type="button"
        disabled={!editor}
        onClick={() => {
          const url = window.prompt("Enter URL");
          if (url) {
            editor?.chain().focus().setLink({ href: url }).run();
          }
        }}
      >
        <LinkIcon size={18} />
      </button>

      <button
        type="button"
        disabled={!editor}
        onClick={() => {
          const url = window.prompt("Image URL");
          if (url) {
            editor?.chain().focus().setImage({ src: url }).run();
          }
        }}
      >
        <ImagePlus size={18} />
      </button>

      <label className="editor-image-upload-btn">
        <ImagePlus size={18} />
        <input type="file" accept="image/*" className="d-none" disabled={!editor} onChange={(e) => handleEditorImageUpload(editor, e)} />
      </label>

      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().undo().run()}>
        <Undo2 size={18} />
      </button>
      <button type="button" disabled={!editor} onClick={() => editor?.chain().focus().redo().run()}>
        <Redo2 size={18} />
      </button>
    </div>
  );

  return (
    <div className="cms-container">
      <h1 className="cms-title">Information Center</h1>
      <p className="cms-subtitle">
        Manage Terms and Conditions, Privacy Policy, About, and FAQ content
      </p>

      <div className="card">
        <h2 className="section-title">Content Management</h2>

        <div className="form-group">
          <label>Select Page Section</label>
          <select
            className={`form-control${sectionError ? " cms-section-select--error" : ""}`}
            value={pageType}
            onChange={handleSectionChange}
            aria-invalid={Boolean(sectionError)}
          >
            <option value="" disabled>
              Select a section
            </option>
            {SECTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {sectionError ? (
            <p className="cms-section-error" role="alert">
              {sectionError}
            </p>
          ) : null}
        </div>

        {pageType && (
          <>
            <p className="mis-preview-note">
              Editing: <strong>{selectedLabel}</strong>
              {loading ? " â€” Loading..." : ""}
            </p>

            {pageType === "terms" && (
              <>
                <div className="form-group">
                  <label>Title (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={termsForm.title}
                    onChange={(e) =>
                      setTermsForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Terms and Conditions"
                  />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <div
                    className={`privacy-policy-editor-wrapper mt-2${contentErrors.content ? " cms-section-select--error" : ""}`}
                  >
                    {renderToolbar(termsEditor)}
                    <div className="privacy-policy-editor-content">
                      <EditorContent editor={termsEditor} />
                    </div>
                  </div>
                  {contentErrors.content ? (
                    <p className="cms-section-error" role="alert">
                      {contentErrors.content}
                    </p>
                  ) : null}
                </div>
              </>
            )}

            {pageType === "privacy" && (
              <>
                <div className="form-group">
                  <label>Title (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={privacyForm.title}
                    onChange={(e) =>
                      setPrivacyForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Privacy Policy"
                  />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <div
                    className={`privacy-policy-editor-wrapper mt-2${contentErrors.content ? " cms-section-select--error" : ""}`}
                  >
                    {renderToolbar(privacyEditor)}
                    <div className="privacy-policy-editor-content">
                      <EditorContent editor={privacyEditor} />
                    </div>
                  </div>
                  {contentErrors.content ? (
                    <p className="cms-section-error" role="alert">
                      {contentErrors.content}
                    </p>
                  ) : null}
                </div>
              </>
            )}

            {pageType === "about" && (
              <>
                <div className="form-group">
                  <label>About Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={aboutForm.title}
                    onChange={(e) =>
                      setAboutForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="About Us"
                  />
                </div>
                <div className="form-group">
                  <label>About Description</label>
                  <div
                    className={`privacy-policy-editor-wrapper mt-2${contentErrors.description ? " cms-section-select--error" : ""}`}
                  >
                    {renderToolbar(aboutEditor)}
                    <div className="privacy-policy-editor-content">
                      <EditorContent editor={aboutEditor} />
                    </div>
                  </div>
                  {contentErrors.description ? (
                    <p className="cms-section-error" role="alert">
                      {contentErrors.description}
                    </p>
                  ) : null}
                </div>
              </>
            )}

            {pageType === "faq" && (
              <div className="cms-subsection">
                <h3 className="subsection-title">FAQ Items</h3>
                <p className="subsection-desc">
                  Add questions and answers shown in the FAQ accordion on the app.
                </p>

                <div className="mis-faq-list">
                  {faqItems.map((item, index) => (
                    <div className="mis-faq-item" key={index}>
                      <div className="mis-faq-item-header">
                        <span className="mis-faq-item-label">FAQ {index + 1}</span>
                        {faqItems.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => removeFaqItem(index)}
                            title="Remove FAQ"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Question</label>
                        <input
                          type="text"
                          className={`form-control${contentErrors.faq?.[index]?.question ? " cms-section-select--error" : ""}`}
                          value={item.question}
                          onChange={(e) =>
                            updateFaqItem(index, "question", e.target.value)
                          }
                          placeholder="Enter question"
                        />
                        {contentErrors.faq?.[index]?.question ? (
                          <p className="cms-section-error" role="alert">
                            {contentErrors.faq[index].question}
                          </p>
                        ) : null}
                      </div>
                      <div className="form-group">
                        <label>Answer</label>
                        <textarea
                          className={`form-control${contentErrors.faq?.[index]?.answer ? " cms-section-select--error" : ""}`}
                          rows={4}
                          value={item.answer}
                          onChange={(e) => updateFaqItem(index, "answer", e.target.value)}
                          placeholder="Enter answer"
                        />
                        {contentErrors.faq?.[index]?.answer ? (
                          <p className="cms-section-error" role="alert">
                            {contentErrors.faq[index].answer}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>

                <button type="button" className="btn-add-row" onClick={addFaqItem}>
                  <FaPlus /> Add FAQ Item
                </button>
              </div>
            )}

            <div className="d-flex gap-3 mt-3">
              <button
                type="button"
                className="marketing__create-btn"
                disabled={saving || loading}
                onClick={handleSave}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MoreInfoSupport;